import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import {
  abortProcess,
  setIsProcessing,
} from 'state/bulkOutletUpdateExpiration/actions';
import ProcessStatus from 'constants/bulkAction/processStatus';
import {
  Button,
  Grid,
  Message,
  Progress,
  Segment,
  Table,
} from 'semantic-ui-react';
import { formatLocalDate } from 'utils/dateTimeUtils';
import { Prompt } from 'react-router-dom';

function ProcessingPage() {
  const dispatch = useDispatch();
  const {
    expirationDate,
    processed,
    outletIds,
    errors,
    currentOutletId,
    status,
    cancelRequested,
  } = useSelector(state => state.bulkOutletUpdateExpiration.process);
  const total = outletIds.length;
  const statusIs = checked => status === checked;

  const renderErrorMessages = messages => {
    if (isEmpty(messages)) {
      return <em>No Error Message</em>;
    }

    return messages.join(', ');
  };

  const onCancel = () => dispatch(abortProcess());

  const renderProgressMessage = () => {
    const percentage = (processed / total) * 100;
    const percentageStr = Math.floor(percentage * 100) / 100;
    switch (status) {
      case ProcessStatus.Completed:
        return 'All Completed Successfully';
      case ProcessStatus.CompletedWithWarning:
        return 'Completed with some errors';
      case ProcessStatus.AllFailed:
        return 'All Failed';
      case ProcessStatus.Aborted:
        return 'Process Aborted, current outlet has already been processed';
      case ProcessStatus.Error:
        return 'Error';
      default:
        return `Processing ${processed} of ${total} (${percentageStr}%)`;
    }
  };

  const isProcessing = useSelector(
    state => state.bulkOutletUpdateExpiration.isProcessing
  );
  const globalError = useSelector(
    state => state.bulkOutletUpdateExpiration.process.globalError
  );
  return (
    <>
      <Prompt
        when={isProcessing && statusIs(ProcessStatus.InProgress)}
        message="Are you sure you want to leave? The process is not yet completed"
      />
      {globalError && <Message error header="Error" content={globalError} />}
      <p>
        Now processing, this may take some time. Please do not close this page
        or navigate away
      </p>
      <Segment>
        <p>
          Processing Outlet ID: <strong>{currentOutletId}</strong>
        </p>
        <p>
          Expiration date: <strong>{formatLocalDate(expirationDate)}</strong>
        </p>
        <Grid columns="equal">
          <Grid.Column>
            <Progress
              progress="ratio"
              value={processed}
              total={total}
              active={statusIs(ProcessStatus.InProgress)}
              warning={statusIs(ProcessStatus.CompletedWithWarning)}
              error={
                statusIs(ProcessStatus.AllFailed) ||
                statusIs(ProcessStatus.Error)
              }
              success={statusIs(ProcessStatus.Completed)}
              disabled={statusIs(ProcessStatus.Aborted)}
            >
              {renderProgressMessage()}
            </Progress>
          </Grid.Column>
          <Grid.Column width={2}>
            {statusIs(ProcessStatus.InProgress) && (
              <Button
                negative
                onClick={onCancel}
                title="Abort"
                disabled={cancelRequested}
              >
                {cancelRequested ? 'Stopping...' : 'Stop'}
              </Button>
            )}
            {!statusIs(ProcessStatus.InProgress) && (
              <Button primary onClick={() => dispatch(setIsProcessing(false))}>
                Reset
              </Button>
            )}
          </Grid.Column>
        </Grid>
      </Segment>
      {!isEmpty(errors) && (
        <>
          <h3>Errors</h3>
          <Table selectable celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Outlet ID</Table.HeaderCell>
                <Table.HeaderCell>Errors</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {errors.map(error => (
                <Table.Row key={error.outletId}>
                  <Table.Cell>{error.outletId}</Table.Cell>
                  <Table.Cell>{renderErrorMessages(error.messages)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      )}
    </>
  );
}

export default ProcessingPage;
